import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { publicRequest, request } from '../../_network/request'
import { clientAccountsURLs, subscriptionsURLs, supportedPaymentsURLs, alertsURLs } from '../../_network/apiUrls'
import { seoData } from '../_common/_helpers/seo_data'
import Header from '../_common/Header'
import HomeContent from './HomeContent'
import HomeProductOpenStack from './HomeProductOpenStack'
import HomeProductISender from './HomeProductISender'
import Footer from '../_common/Footer'
import CustomDialog from '../_common/CustomDialog'
import LoginWrapper from '../_common/LoginWrapper'
import SignUpWrapper from '../_common/SignUpWrapper'
import PasswordResetWrapper from '../_common/PasswordResetWrapper'
import PasswordResetConfirmWrapper from '../_common/PasswordResetConfirmWrapper'
import ValidationEmailResendWrapper from '../_common/ValidationEmailResendWrapper'
import SubscriptionAddWrapper from '../subscriptions/add/SubscriptionAddWrapper'
import SubscriptionSummaryWrapper from '../subscriptions/summary/SubscriptionSummaryWrapper'
import SubscriptionEditWrapper from '../subscriptions/edit/SubscriptionEditWrapper'
import SubscriptionsViewWrapper from '../subscriptions/view/SubscriptionsViewWrapper'
import NoSubscriptionWrapper from '../subscriptions/edit/NoSubscriptionWrapper'
import UsersWrapper from '../users/UsersWrapper'
import ProfileWrapper from '../profile/ProfileWrapper'
import NotificationsWrapper from '../notifications/NotificationsWrapper'
import DocsWrapper from '../docs/DocsWrapper'
import ISenderWrapper from '../subscriptions/isender/ISenderWrapper'
import CookiesPopup from '../_common/CookiesPopup'
import CustomHelmet from '../_common/CustomHelmet'

const HomeWrapper = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { userId, validationString } = useParams()
  
  const cookies_status = useSelector(state => state.settings.cookies_status)
  const isAuthenticated = useSelector(state => state.profile.isAuthenticated)
  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'
  
  const [loginDialogOpen, setLoginDialogOpen] = useState(false)
  const [signUpDialogOpen, setSignUpDialogOpen] = useState(false)
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false)
  const [passwordResetConfirmDialogOpen, setPasswordResetConfirmDialogOpen] = useState(false)
  const [passwordResetUserId, setPassworResetUserId] = useState(null)
  const [passwordResetValidationString, setPasswordResetValidationString] = useState(null)
  const [validationEmailResendDialogOpen, setValidationEmailResendDialogOpen] = useState(false)  
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false)
  const [notificationDialogProps, setNotificationDialogProps] = useState(null)
  const [loginNotification, setLoginNotification] = useState(null)

  const [clientSubscriptions, setClientSubscriptions] = useState([])
  const [fetchSubscriptions, setFetchSubscriptions] = useState(1)
  const [pageType, setPageType] = useState(null)
  const [subscriptionGroupNames, setSubscriptionGroupNames] = useState([])
  const [subscriptionGroupsData, setSubscriptionGroupsData] = useState([])
  const [supportedPaymentMethods, setSupportedPaymentMethods] = useState([])
  const [latestAlerts, setLatestAlerts] = useState([])

  const wrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 200px)',
  }

  const handleNotificationDialogOpen = (dialogProps) => {
    setNotificationDialogOpen(true)
    setNotificationDialogProps(dialogProps)
  }

  const handleNotificationDialogClose = () => {
    setNotificationDialogOpen(false)
    setNotificationDialogProps(null)
  }

  useEffect(() => {
    const confirmEmail = async () => {
      const url = constants.core_service_domain + '/' + clientAccountsURLs.getUsersEmailConfirm

      const postData = {
        user_id: userId,
        validation_string: validationString,
      }
      
      const request_data = { url, method: 'PUT', data: postData }
    
      const response = await publicRequest(request_data)
      
      if (response && response.status_code && response.status_code < 400 && response.data && response.data.status && response.data.status === 'confirmed') {
        setLoginDialogOpen(true)
        setLoginNotification(defaultTexts.yourAccountIsActivated)
      } else {
        handleNotificationDialogOpen({
          text: defaultTexts.anErrorOccured,
        })
      }
    }

    if (userId && validationString && location.pathname.includes('/email-validation')) {
      confirmEmail()      
      navigate('/')
    }

    if (userId && validationString && location.pathname.includes('/password-reset')) {
      setPasswordResetConfirmDialogOpen(true)
      setPassworResetUserId(userId)
      setPasswordResetValidationString(validationString)
      navigate('/')
    }
  }, [userId, validationString, navigate, location.pathname])

  useEffect(() => {
    const fetchClientSubscriptions = async () => {
      const url = constants.core_service_domain + '/' + subscriptionsURLs.getClientSubscriptions

      const requestData = { url, method: 'GET' }

      const response = await request(requestData)
      
      if (response && response.status_code && response.status_code < 400 && response.data && response.data.length > 0) {
        const active_subscriptions = response.data.filter(item => item.status === 'active')
        setClientSubscriptions(active_subscriptions)
        setPageType(active_subscriptions.length > 0 ? 'current' : 'new')

        let subscription_names = []
        active_subscriptions.forEach(item => subscription_names.push(item.package_group))
        setSubscriptionGroupNames(subscription_names)
      } else {
        setClientSubscriptions([])
        setSubscriptionGroupNames([])
        setPageType('new')
      }
    }

    if (isAuthenticated) {
      fetchClientSubscriptions()
    } else {
      setPageType(null)
    }    
  }, [isAuthenticated, fetchSubscriptions])

  useEffect(() => {
    const fetchSubscriptionGroupsData = async () => {
      const url = constants.core_service_domain + '/' + subscriptionsURLs.getSubscriptionGroups
      
      const request_data = { url, method: 'GET' }
      
      const response = await request(request_data)
      
      if (response.status_code && response.status_code < 400 && response.data && response.data.length > 0) {
        setSubscriptionGroupsData(response.data)
      } else {
        setSubscriptionGroupsData([])
      }
    }

    if (isAuthenticated) {
      fetchSubscriptionGroupsData()
    } else {
      setSubscriptionGroupsData([])
    }    
  }, [isAuthenticated])

  useEffect(() => {
    if (location.state && location.state.fetch_subscriptions) {
			setFetchSubscriptions(prev => prev + 1)
			navigate('/', {})
		}
  }, [navigate, location])

  useEffect(() => {
    const fetchSupportedPaymentMehodsData = async () => {
      const url = constants.billing_url + '/' + supportedPaymentsURLs.getSupportedPaymentMethods
      
      const request_data = { url, method: 'GET' }
      
      const response = await request(request_data)
      
      if (response.status_code && response.status_code < 400 && response.data && response.data.length > 0) {
        setSupportedPaymentMethods(response.data)
      } else {
        setSupportedPaymentMethods([])
      }
    }

    if (isAuthenticated) {
      fetchSupportedPaymentMehodsData()
    } else {
      setSupportedPaymentMethods([])
    }
  }, [isAuthenticated])
  
  useEffect(() => {
    const fetchLatestAlertsData = async () => {
      const url = constants.core_service_domain + '/' + alertsURLs.getAlerts + '?limit=' + 5
      
      const request_data = { url, method: 'GET' }
      
      const response = await request(request_data)
      
      if (response.status_code && response.status_code < 400 && response.data && response.data.length > 0) {
        setLatestAlerts(response.data)
      } else {
        setLatestAlerts([])
      }
    }

    if (isAuthenticated) {
      fetchLatestAlertsData()
    } else {
      setLatestAlerts([])
    }    
  }, [isAuthenticated])
  
  return (
    <>
      <CustomHelmet
        url={location.pathname}
        title={seoData[location.pathname] ? seoData[location.pathname].title : ''}
        description={seoData[location.pathname] ? seoData[location.pathname].description : ''}
      />

      <Box sx={wrapperStyle}>
        <Header
          setLoginDialogOpen={setLoginDialogOpen}
          setSignUpDialogOpen={setSignUpDialogOpen}
          supportedPaymentMethods={supportedPaymentMethods}
          subscriptionGroupsData={subscriptionGroupsData}
        />
        {
          isAuthenticated ?
          <>
            {
              location.pathname === '/' &&
              <>
                {
                  pageType === 'new' ?
                  <>
                    {
                      editPermission ?
                      <SubscriptionAddWrapper
                        supportedPaymentMethods={supportedPaymentMethods}
                        subscriptionGroupNames={subscriptionGroupNames}
                      /> :
                      <SubscriptionsViewWrapper
                        clientSubscriptions={clientSubscriptions}
                        subscriptionGroupsData={subscriptionGroupsData}
                        setFetchSubscriptions={setFetchSubscriptions}
                      />
                    }
                  </> :
                  <SubscriptionSummaryWrapper
                    clientSubscriptions={clientSubscriptions}
                    subscriptionGroupsData={subscriptionGroupsData}
                    latestAlerts={latestAlerts}
                  />
                }
              </>
            }

            {
              location.pathname === '/add-new' &&
              <SubscriptionAddWrapper
                supportedPaymentMethods={supportedPaymentMethods}
                subscriptionGroupNames={subscriptionGroupNames}
              />
            }

            {
              location.pathname === '/openstack-solution-kit' &&
              <>
                {
                  (pageType === 'current' && subscriptionGroupNames.includes('openstack_admin')) &&
                  <SubscriptionEditWrapper
                    clientSubscriptions={clientSubscriptions}
                    subscriptionGroupsData={subscriptionGroupsData}
                    subscriptionGroupNames={subscriptionGroupNames}
                    latestAlerts={latestAlerts}
                  />
                }

                {
                  (pageType && !subscriptionGroupNames.includes('openstack_admin')) &&
                  <NoSubscriptionWrapper
                    subscriptionGroupsData={subscriptionGroupsData}
                    groupName='openstack_admin'
                  />
                }              
              </>              
            }

            {
              location.pathname === '/isender' &&
              <>
                {
                  (pageType === 'current' && subscriptionGroupNames.includes('isender')) &&
                  <ISenderWrapper
                    clientSubscriptions={clientSubscriptions}
                    subscriptionGroupsData={subscriptionGroupsData}
                    latestAlerts={latestAlerts}
                  />
                }

                {
                  (pageType && !subscriptionGroupNames.includes('isender')) &&
                  <NoSubscriptionWrapper
                    subscriptionGroupsData={subscriptionGroupsData}
                    groupName='isender'
                  />
                }
              </>              
            }

            {
              location.pathname === '/subscriptions' &&
              <SubscriptionsViewWrapper
                clientSubscriptions={clientSubscriptions}
                subscriptionGroupsData={subscriptionGroupsData}
                setFetchSubscriptions={setFetchSubscriptions}
              />
            }

            {
              location.pathname === '/users' &&
              <UsersWrapper />
            }

            {
              location.pathname === '/profile' &&
              <ProfileWrapper />
            }

            {
              location.pathname === '/notifications' &&
              <NotificationsWrapper />
            }

            {
              location.pathname === '/docs' &&
              <DocsWrapper />
            }
          </> :
          <>
            {
              location.pathname === '/' &&
              <HomeContent />
            }

            {
              location.pathname === '/products/openstack-solution-kit' &&
              <HomeProductOpenStack />
            }

            {
              location.pathname === '/products/isender' &&
              <HomeProductISender />
            }
          </>          
        }
      </Box>      
      <Footer />

      <CustomDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        dialogTitle={{
          title: defaultTexts.login, 
          sx: {
            color: theme.palette.violetsAreBlue,
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: '26px',
          }
        }}
        backgroundColor={theme.palette.oxfordBlue}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <LoginWrapper
          loginNotification={loginNotification}
          setLoginDialogOpen={setLoginDialogOpen}
          setSignUpDialogOpen={setSignUpDialogOpen}
          setPasswordResetDialogOpen={setPasswordResetDialogOpen}
          setValidationEmailResendDialogOpen={setValidationEmailResendDialogOpen}
        />
      </CustomDialog>

      <CustomDialog
        open={signUpDialogOpen}
        onClose={() => setSignUpDialogOpen(false)}
        dialogTitle={{
          title: defaultTexts.signUp, 
          sx: {
            color: theme.palette.violetsAreBlue,
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: '26px',
          }
        }}
        backgroundColor={theme.palette.oxfordBlue}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <SignUpWrapper
          setLoginDialogOpen={setLoginDialogOpen}
          setSignUpDialogOpen={setSignUpDialogOpen} 
          handleNotificationDialogOpen={handleNotificationDialogOpen}
        />
      </CustomDialog>

      <CustomDialog
        open={passwordResetDialogOpen}
        onClose={() => setPasswordResetDialogOpen(false)}
        dialogTitle={{
          title: defaultTexts.resetPassword, 
          sx: {
            color: theme.palette.violetsAreBlue,
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: '26px',
          }
        }}
        backgroundColor={theme.palette.oxfordBlue}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <PasswordResetWrapper
          setLoginDialogOpen={setLoginDialogOpen}
          setPasswordResetDialogOpen={setPasswordResetDialogOpen}
          handleNotificationDialogOpen={handleNotificationDialogOpen}
        />
      </CustomDialog>

      <CustomDialog
        open={passwordResetConfirmDialogOpen}
        onClose={() => setPasswordResetConfirmDialogOpen(false)}
        dialogTitle={{
          title: defaultTexts.resetPassword, 
          sx: {
            color: theme.palette.violetsAreBlue,
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: '26px',
          }
        }}
        backgroundColor={theme.palette.oxfordBlue}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <PasswordResetConfirmWrapper
          userId={passwordResetUserId}
          validationString={passwordResetValidationString}
          setLoginDialogOpen={setLoginDialogOpen}
          setLoginNotification={setLoginNotification}
          setPasswordResetConfirmDialogOpen={setPasswordResetConfirmDialogOpen}          
        />
      </CustomDialog>

      <CustomDialog
        open={validationEmailResendDialogOpen}
        onClose={() => setValidationEmailResendDialogOpen(false)}
        dialogTitle={{
          title: defaultTexts.resendValidationEmail, 
          sx: {
            color: theme.palette.violetsAreBlue,
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: '26px',
          }
        }}
        backgroundColor={theme.palette.oxfordBlue}
        closeIconColor={theme.palette.violetsAreBlue}
      >
        <ValidationEmailResendWrapper
          setLoginDialogOpen={setLoginDialogOpen}
          setValidationEmailResendDialogOpen={setValidationEmailResendDialogOpen}
          handleNotificationDialogOpen={handleNotificationDialogOpen}
        />
      </CustomDialog>

      <CustomDialog
        open={notificationDialogOpen}
        onClose={handleNotificationDialogClose}
        dialogTitle={{
          title: (notificationDialogProps && notificationDialogProps.title) ? notificationDialogProps.title : '',
          sx: {
            color: theme.palette.violetsAreBlue,
            textTransform: 'uppercase',
            textAlign: 'center',
            fontSize: '26px',
          }
        }}
        dialogBody={{
          text: (notificationDialogProps && notificationDialogProps.text) ? notificationDialogProps.text : '',
          sx: {
            color: 'white',
            textAlign: 'center',
            fontSize: '18px',
          }
        }}
        backgroundColor={theme.palette.oxfordBlue}
        closeIconColor={theme.palette.violetsAreBlue}
      />

      { !cookies_status && <CookiesPopup /> }
    </>
  )
}

export default HomeWrapper